import { useRef, useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import axios from '../../api/axios';
import { Alert, CircularProgress } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import 'bootstrap/dist/css/bootstrap.min.css';
import AuthPageLayout from './AuthPageLayout';

const RESET_PASSWORD_URL = '/Registration/ResetPassword';

const ResetPassword = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const passwordRef = useRef();
    const errRef = useRef();

    // Get token and email from URL params
    const token = searchParams.get('token') || '';
    const email = searchParams.get('email') || '';

    const [formData, setFormData] = useState({
        newPassword: '',
        confirmPassword: ''
    });
    const [errMsg, setErrMsg] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    // Password validation states
    const [validPassword, setValidPassword] = useState(false);
    const [passwordFocus, setPasswordFocus] = useState(false);

    // Password requirements regex
    const PWD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

    useEffect(() => {
        if (passwordRef.current) {
            passwordRef.current.focus();
        }
    }, []);

    useEffect(() => {
        setValidPassword(PWD_REGEX.test(formData.newPassword));
    }, [formData.newPassword]);

    useEffect(() => {
        setErrMsg('');
    }, [formData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate password match
        if (formData.newPassword !== formData.confirmPassword) {
            setErrMsg(t('passwordsMustMatch'));
            return;
        }

        // Validate password strength
        if (!PWD_REGEX.test(formData.newPassword)) {
            setErrMsg(t('registerPasswordRequirements'));
            return;
        }

        setIsLoading(true);
        setErrMsg('');

        try {
            await axios.post(
                RESET_PASSWORD_URL,
                {
                    email,
                    token,
                    newPassword: formData.newPassword,
                    confirmPassword: formData.confirmPassword
                },
                {
                    headers: { 'Content-Type': 'application/json' }
                }
            );
            setIsSubmitted(true);
        } catch (err) {
            if (!err?.response) {
                setErrMsg(t('errorNoServerResponse'));
            } else if (err.response?.status === 400) {
                const message = err.response?.data?.message;
                if (message?.includes('expired') || message?.includes('Invalid')) {
                    setErrMsg(t('resetPasswordTokenExpired'));
                } else {
                    setErrMsg(message || t('resetPasswordError'));
                }
            } else {
                setErrMsg(t('resetPasswordError'));
            }
            if (errRef.current) {
                errRef.current.focus();
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Show error if token or email is missing
    if (!token || !email) {
        return (
            <AuthPageLayout>
                <div
                    className="card shadow"
                    style={{ width: '100%', maxWidth: '450px', border: 'none', borderRadius: '0.5rem' }}
                >
                    <div className="card-body text-center p-4">
                        <Link to="/Home">
                            <img
                                src="/assets/img/OnlineCADCOM_FullLogo.png"
                                alt="Online-CADCOM"
                                style={{ height: '50px', marginBottom: '16px' }}
                            />
                        </Link>
                        <ErrorOutlineIcon sx={{ fontSize: 60, color: '#dc3545', mb: 2 }} />
                        <h3 style={{ color: '#333' }}>{t('resetPasswordInvalidLinkTitle')}</h3>
                        <p className="text-muted mb-4">{t('resetPasswordInvalidLinkMessage')}</p>
                        <Link
                            to="/forgot-password"
                            className="btn btn-lg w-100 mb-2"
                            style={{ backgroundColor: '#112d4e', color: '#fff', border: 'none' }}
                        >
                            {t('requestNewResetLink')}
                        </Link>
                        <Link
                            to="/login"
                            className="btn btn-outline-secondary btn-lg w-100"
                        >
                            {t('backToLogin')}
                        </Link>
                    </div>
                </div>
            </AuthPageLayout>
        );
    }

    if (isSubmitted) {
        return (
            <AuthPageLayout>
                <div
                    className="card shadow"
                    style={{ width: '100%', maxWidth: '450px', border: 'none', borderRadius: '0.5rem' }}
                >
                    <div className="card-body text-center p-4">
                        <Link to="/Home">
                            <img
                                src="/assets/img/OnlineCADCOM_FullLogo.png"
                                alt="Online-CADCOM"
                                style={{ height: '50px', marginBottom: '16px' }}
                            />
                        </Link>
                        <CheckCircleIcon sx={{ fontSize: 60, color: '#28a745', mb: 2 }} />
                        <h3 style={{ color: '#333' }}>{t('resetPasswordSuccessTitle')}</h3>
                        <p className="text-muted mb-4">{t('resetPasswordSuccessMessage')}</p>
                        <Link
                            to="/login"
                            className="btn btn-lg w-100"
                            style={{ backgroundColor: '#112d4e', color: '#fff', border: 'none' }}
                        >
                            {t('loginNow')}
                        </Link>
                    </div>
                </div>
            </AuthPageLayout>
        );
    }

    return (
        <AuthPageLayout>
            <div
                className="card shadow"
                style={{ width: '100%', maxWidth: '450px', border: 'none', borderRadius: '0.5rem' }}
            >
                <div className="card-body">
                    <div className="text-center mb-4">
                        <Link to="/Home">
                            <img
                                src="/assets/img/OnlineCADCOM_FullLogo.png"
                                alt="Online-CADCOM"
                                style={{ height: '60px' }}
                            />
                        </Link>
                        <p className="text-muted mt-3">{t("resetPasswordSubtitle")}</p>
                    </div>

                    {errMsg && (
                        <Alert
                            ref={errRef}
                            variant="filled"
                            severity="error"
                            className="mb-3"
                            tabIndex={-1}
                            action={
                                <IconButton
                                    aria-label="close"
                                    color="inherit"
                                    size="small"
                                    onClick={() => setErrMsg('')}
                                >
                                    <CloseIcon fontSize="inherit" />
                                </IconButton>
                            }
                        >
                            {errMsg}
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="form-floating mb-3">
                            <input
                                type="password"
                                className="form-control"
                                id="newPassword"
                                name="newPassword"
                                placeholder={t("resetPasswordNewPassword")}
                                ref={passwordRef}
                                onChange={handleChange}
                                value={formData.newPassword}
                                required
                                minLength={8}
                                maxLength={100}
                                autoComplete="new-password"
                                onFocus={() => setPasswordFocus(true)}
                                onBlur={() => setPasswordFocus(false)}
                            />
                            <label htmlFor="newPassword">{t("resetPasswordNewPassword")}</label>
                        </div>

                        {passwordFocus && !validPassword && (
                            <div className="alert alert-info py-2 small mb-3">
                                {t("registerPasswordHint")}
                            </div>
                        )}

                        <div className="form-floating mb-3">
                            <input
                                type="password"
                                className="form-control"
                                id="confirmPassword"
                                name="confirmPassword"
                                placeholder={t("resetPasswordConfirmPassword")}
                                onChange={handleChange}
                                value={formData.confirmPassword}
                                required
                                autoComplete="new-password"
                            />
                            <label htmlFor="confirmPassword">{t("resetPasswordConfirmPassword")}</label>
                        </div>

                        <button
                            className="btn btn-lg w-100"
                            type="submit"
                            disabled={isLoading}
                            style={{ backgroundColor: '#112d4e', color: '#fff', border: 'none' }}
                        >
                            {isLoading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                t("resetPasswordSubmitButton")
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </AuthPageLayout>
    );
};

export default ResetPassword;
